import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Sprout, Warehouse, ShieldCheck, Recycle, TrendingUp } from "lucide-react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import api, { imageUrl } from "@/lib/api";

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [wholesale, setWholesale] = useState([]);
  const [agri, setAgri] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/listings?featured=true&limit=10"),
      api.get("/listings?is_wholesale=true&limit=8"),
      api.get("/listings?is_agriculture=true&limit=8"),
    ]).then(([c, f, w, a]) => {
      setCategories(c.data.items);
      setFeatured(f.data.items);
      setWholesale(w.data.items);
      setAgri(a.data.items);
    }).finally(() => setLoading(false));
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(`/ilanlar?q=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-emerald-900 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #10b981 0, transparent 40%), radial-gradient(circle at 80% 60%, #065f46 0, transparent 45%)" }} />
        <div className="relative mx-auto grid max-w-7xl gap-8 px-6 py-14 lg:grid-cols-5 lg:py-20">
          <div className="lg:col-span-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-800/60 px-3 py-1 text-xs font-semibold text-emerald-200">
              <TrendingUp size={14} /> Birinin fazlası, diğerinin ihtiyacı
            </span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
              Fazlan çöpe gitmesin,<br /><span className="text-emerald-400">değere dönüşsün.</span>
            </h1>
            <p className="mt-4 max-w-xl text-base text-emerald-100 sm:text-lg">
              Stok fazlasından tarlada kalan ürüne, ikinci elden sanayi malzemesine kadar binlerce fırsatı keşfet.
            </p>
            <form onSubmit={search} className="mt-7 flex max-w-xl flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hero-search-input"
                  placeholder="Ne arıyorsunuz?" className="h-13 rounded-full border-0 bg-white py-6 pl-12 text-base text-slate-900 focus-visible:ring-2 focus-visible:ring-emerald-400" />
              </div>
              <Button type="submit" size="lg" data-testid="hero-search-btn"
                className="h-auto rounded-full bg-emerald-500 py-3 font-semibold text-emerald-950 hover:bg-emerald-400 sm:px-8">
                Ürünleri Keşfet
              </Button>
            </form>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/panel/yeni-ilan")} variant="ghost" data-testid="hero-create-btn"
                className="gap-1.5 rounded-full border border-emerald-700 text-white hover:bg-emerald-800">
                Ücretsiz İlan Ver <ArrowRight size={16} />
              </Button>
            </div>
          </div>

          <div className="hidden gap-3 lg:col-span-2 lg:grid lg:grid-cols-2">
            <HeroTile icon={Sprout} title="Tarlada Kalanlar" to="/tarlada-kalanlar" className="row-span-2 bg-emerald-800/50" big />
            <HeroTile icon={Warehouse} title="Toptan Fırsatlar" to="/toptan-firsatlar" className="bg-emerald-800/50" />
            <HeroTile icon={ShieldCheck} title="Kurumsal Satıcılar" to="/kurumsal-saticilar" className="bg-emerald-800/50" />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <SectionHeader title="Kategoriler" subtitle="Aradığınız fırsatı kategoriye göre bulun" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8">
          {categories.map((cat) => {
            const Icon = Icons[cat.icon] || Recycle;
            return (
              <Link key={cat.slug} to={`/ilanlar?category=${cat.slug}`} data-testid={`category-${cat.slug}`}
                className="group flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-center transition-all hover:border-emerald-300 hover:bg-emerald-50">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 transition-colors group-hover:bg-emerald-600 group-hover:text-white">
                  <Icon size={22} />
                </span>
                <span className="text-xs font-medium leading-tight text-slate-700">{cat.name}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeader title="Öne Çıkan Fırsatlar" subtitle="Editör seçimi ilanlar" link="/ilanlar" />
          <Row loading={loading} listings={featured} />
        </div>
      </section>

      {/* Agriculture banner */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="relative overflow-hidden rounded-2xl bg-emerald-900 p-8 text-white md:p-12">
          <img src={imageUrl("https://images.unsplash.com/photo-1560493676-04071c5f467b?w=1200&q=80")} alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="relative max-w-xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-emerald-950">
              <Sprout size={14} /> ÜRETİCİ DOSTU
            </span>
            <h2 className="mt-3 font-display text-2xl font-extrabold sm:text-3xl">Tarlada kalan ürünler değer bulsun</h2>
            <p className="mt-2 text-emerald-100">Hasat edilemeyen veya piyasa fiyatı maliyetin altına düşen ürünlerinizi hızlıca alıcıyla buluşturun.</p>
            <Button onClick={() => navigate("/tarlada-kalanlar")} data-testid="agri-cta"
              className="mt-5 gap-1.5 rounded-full bg-white font-semibold text-emerald-900 hover:bg-emerald-50">
              Tarlada Kalanları Gör <ArrowRight size={16} />
            </Button>
          </div>
        </div>
        <div className="mt-8"><Row loading={loading} listings={agri} /></div>
      </section>

      {/* Wholesale */}
      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeader title="Toptan Fırsatlar" subtitle="Palet, lot ve depo fazlası ürünler" link="/toptan-firsatlar" />
          <Row loading={loading} listings={wholesale} />
        </div>
      </section>
    </div>
  );
}

function HeroTile({ icon: Icon, title, to, className, big }) {
  return (
    <Link to={to} className={`flex flex-col justify-between rounded-2xl border border-emerald-700/50 p-5 transition-colors hover:bg-emerald-700/60 ${className}`}>
      <Icon className={`text-emerald-300 ${big ? "h-8 w-8" : "h-6 w-6"}`} />
      <span className={`font-display font-bold ${big ? "text-xl" : "text-base"}`}>{title}</span>
    </Link>
  );
}

function SectionHeader({ title, subtitle, link }) {
  return (
    <div className="mb-6 flex items-end justify-between">
      <div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {link && (
        <Link to={link} className="flex shrink-0 items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline">
          Tümü <ArrowRight size={15} />
        </Link>
      )}
    </div>
  );
}

function Row({ loading, listings }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => <ProductCardSkeleton key={i} />)}
      </div>
    );
  }
  if (!listings.length) return <p className="text-sm text-slate-500">Henüz ilan yok.</p>;
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
      {listings.slice(0, 5).map((l) => <ProductCard key={l.listing_id} listing={l} />)}
    </div>
  );
}
